import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Divider } from 'primereact/divider'

import { PermissionSuffix, VatRecordDto } from '../../_generated/api/user-management'
import { useUserContext } from '../contexts/UserContext'
import { useToastContext } from '../contexts/ToastContext'
import FlowCard from '../flow-card/FlowCard'
import FlowCardStep, { FlowCardStepProps } from '../flow-card/FlowCardStep'
import { FlowCardStepData } from '../flow-card/FlowCardStepData'
import FlowCardSideBar from '../flow-card/FlowCardSideBar'
import { PermissionSwitchList } from '../common/pure/SwitchList'
import { DropdownField, TextField } from '../common/pure/input-fields'
import { SingleContentColumn } from '../common/pure/layout'
import { Column } from '../common/pure/FlexContainer'
import ToastOnce from '../common/ToastOnce'
import t from '../../tools/t'

type CreateUserStepType = 'invite' | 'permission'
interface CreateUserStepData extends FlowCardStepData {
    key: CreateUserStepType
}

export default function CreateUserScreen() {
    const navigate = useNavigate()
    const { createUser, selectedOrganization, createUserLoading, createUserError } =
        useUserContext()
    const Toast = useToastContext()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [association, setAssociation] = useState('')
    const [selectedPermissions, setSelectedPermissions] = useState<PermissionSuffix[]>([])
    const [currentStepIndex, setCurrentStepIndex] = useState(0)

    useEffect(() => {
        if (createUserError) Toast.error(createUserError)
    }, [createUserLoading, createUserError])

    const navigateUsers = () => navigate('/users')
    const onStepClick = (index: number) => setCurrentStepIndex(index)
    const onCancel = () => navigateUsers()
    const onNext = () => setCurrentStepIndex(currentStepIndex + 1)
    const onCreate = async () => {
        if (!association) return
        const user = await createUser({
            vatNo: association,
            name,
            email,
            permissions: selectedPermissions,
        })
        if (!user) return
        Toast.completed('create', 'user')
        navigateUsers()
    }

    const steps: CreateUserStepData[] = [
        {
            key: 'invite',
            title: t('user_create_invite_step_title'),
            subtitle: t('user_create_invite_step_subtitle'),
            label: t('user_create_invite_step_menu_label'),
        },
        {
            key: 'permission',
            title: t('user_create_permissions_step_title', name),
            subtitle: t('user_create_permissions_step_subtitle', name),
            label: t('user_create_permissions_step_menu_label'),
        },
    ]

    const currentStep = steps[currentStepIndex]
    // NOTE: Currently, name and email are the only fields necessary to create a user.
    //  Permissions and workflows are not required.
    const isValid = !!(name && email && association)

    return (
        <SingleContentColumn columns={3}>
            <FlowCard
                steps={steps.length}
                current={currentStepIndex}
                onCancel={onCancel}
                onNext={onNext}
                onDone={onCreate}
                valid={isValid}
                aside={
                    <FlowCardSideBar
                        title={t('user_create_flow_title')}
                        icon="users"
                        steps={steps}
                        onStepClick={onStepClick}
                        currentStepIndex={currentStepIndex}
                    />
                }
                currentStep={(() => {
                    switch (currentStep.key) {
                        case 'invite':
                            return (
                                <CreateUserIdentityStep
                                    title={currentStep.title}
                                    subtitle={currentStep.subtitle}
                                    name={name}
                                    setName={setName}
                                    email={email}
                                    setEmail={setEmail}
                                    association={association}
                                    associationRecords={selectedOrganization?.associations ?? []}
                                    setAssociation={setAssociation}
                                />
                            )
                        case 'permission':
                            return (
                                <SelectUserPermissionStep
                                    selectedPermissions={selectedPermissions}
                                    onPermissionsChange={(permissions) =>
                                        setSelectedPermissions(permissions)
                                    }
                                    title={currentStep.title}
                                    subtitle={currentStep.subtitle}
                                />
                            )
                        default:
                            return undefined
                    }
                })()}
            />
            <ToastOnce key="createUserError" error={createUserError} />
        </SingleContentColumn>
    )
}

interface CreateUserIdentityStepProps extends FlowCardStepProps {
    name: string
    email: string
    setName: (name: string) => void
    setEmail: (email: string) => void
    association?: string
    associationRecords: VatRecordDto[]
    setAssociation: (record: string) => void
}
const CreateUserIdentityStep = ({
    name,
    email,
    setName,
    setEmail,
    association,
    setAssociation,
    associationRecords,
    ...props
}: CreateUserIdentityStepProps) => {
    const associationSource = associationRecords.map((r) => ({
        label: `${r.vatNo}\t${r.name}`,
        value: r.vatNo,
    }))

    return (
        <FlowCardStep {...props}>
            <Column gap={1}>
                <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('user_field_name')}
                />
                <TextField
                    keyfilter="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('user_field_email')}
                />
                <DropdownField
                    value={association}
                    options={associationSource}
                    onChange={(data) => setAssociation(data.value)}
                    placeholder={t('user_field_association')}
                />
                <Divider unstyled />
            </Column>
        </FlowCardStep>
    )
}

interface SelectUserPermissionStepProps extends FlowCardStepProps {
    selectedPermissions: PermissionSuffix[]
    onPermissionsChange: (permissions: PermissionSuffix[]) => void
}
const SelectUserPermissionStep = ({
    selectedPermissions,
    onPermissionsChange,
    ...props
}: SelectUserPermissionStepProps) => {
    return (
        <FlowCardStep {...props}>
            <PermissionSwitchList
                selectedPermissions={selectedPermissions}
                onChange={onPermissionsChange}
            />
        </FlowCardStep>
    )
}
